import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { installFlushOnHide, track } from './analytics';
import {
  DEFAULT_LOCALE,
  LOCALE_CONFIG,
  LOCALES,
  MESSAGES,
  buildHash,
  formatDate,
  formatInt,
  formatMoney,
  formatRatio,
  parseHash,
  pick,
  switchLocaleInHash,
  type BiText,
  type Locale,
} from './i18n-core';

export interface I18nValue {
  locale: Locale;
  /** 当前路由路径（不含语言前缀） */
  path: string;
  setLocale: (locale: Locale) => void;
  navigate: (to: string) => void;
  /** 取共享 UI 文案 */
  t: (key: string) => string;
  /** 取多语言业务字段 */
  pick: (text: BiText | null | undefined) => string;
  /** 金额：null/undefined 表示「待定」（业务方未确认对外口径），此时返回 state.pending 文案 */
  money: (value: number | null | undefined) => string;
  ratio: (value: number, digits?: number) => string;
  num: (value: number) => string;
  date: (iso: string) => string;
}

export const I18nContext = createContext<I18nValue | null>(null);

const LOCALE_KEY = 'longnan.locale';

/** 记住上次选择的语言：跨站跳转、直接打开根地址时都沿用，避免退回默认语言 */
function readStoredLocale(): Locale | null {
  try {
    const v = localStorage.getItem(LOCALE_KEY);
    return v && (LOCALES as readonly string[]).includes(v) ? (v as Locale) : null;
  } catch {
    return null;
  }
}

function storeLocale(l: Locale): void {
  try {
    localStorage.setItem(LOCALE_KEY, l);
  } catch {
    // 隐私模式或存储被禁用：忽略即可，不影响功能
  }
}

/** hash 中是否显式带了语言前缀（用于区分「用户选了中文」与「没选过」） */
function hashLocale(hash: string): Locale | null {
  const m = /^#\/(zh|en|vi|th|id|ms|km|ja)(?:\/|$)/.exec(hash);
  return m ? (m[1] as Locale) : null;
}

function readHash(): string {
  return window.location.hash || '#/';
}

/**
 * [gemini-i18n-core] 语言与路由合一：语言是 hash 的第一个路径段（#/en/ticketing, #/vi/services 等）。
 * 这样链接可分享、静态产物放任意路径都可用，也不需要服务端重写规则。
 */
export function I18nProvider({ children, site = 'unknown' }: { children: ReactNode; site?: string }) {
  const [hash, setHash] = useState<string>(() => readHash());

  useEffect(() => {
    const onChange = () => setHash(readHash());
    window.addEventListener('hashchange', onChange);
    installFlushOnHide();
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  const { locale, path } = useMemo(() => parseHash(hash), [hash]);

  // 首次进入且 hash 未带语言前缀时，沿用上次选择的语言（跨站跳转 / 直接打开根地址）
  useEffect(() => {
    const h = readHash();
    if (hashLocale(h) !== null) return;
    const stored = readStoredLocale();
    if (stored && stored !== DEFAULT_LOCALE) window.location.hash = switchLocaleInHash(h, stored);
  }, []);

  // 经跨站链接带着语言进来时，同步记住该语言
  useEffect(() => {
    if (hashLocale(hash) !== null) storeLocale(locale);
  }, [hash, locale]);

  const navigate = useCallback(
    (to: string) => {
      const next = `#${buildHash(locale, to)}`;
      if (readHash() === next) return;
      track('nav_click', site, { path: to, locale });
      window.location.hash = next;
      window.scrollTo({ top: 0 });
    },
    [locale, site],
  );

  const setLocale = useCallback(
    (next: Locale) => {
      const target = switchLocaleInHash(readHash(), next);
      if (readHash() === target) return;
      track('lang_switch', site, { path, locale, props: { to: next } });
      storeLocale(next);
      window.location.hash = target;
    },
    [locale, path, site],
  );

  // 页面浏览（含语言变化）：path/locale 变化即记一次
  useEffect(() => {
    track('site_view', site, { path, locale });
  }, [site, path, locale]);

  // [gemini-i18n-core] 同步 <html lang> 8 国语言映射，便于浏览器断词与屏幕阅读器
  useEffect(() => {
    const langMap: Record<Locale, string> = {
      zh: 'zh-CN',
      en: 'en',
      vi: 'vi-VN',
      th: 'th-TH',
      id: 'id-ID',
      ms: 'ms-MY',
      km: 'km-KH',
      ja: 'ja-JP',
    };
    document.documentElement.lang = langMap[locale] || 'zh-CN';
  }, [locale]);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      path,
      setLocale,
      navigate,
      t: (key: string) => MESSAGES[locale]?.[key] ?? MESSAGES[DEFAULT_LOCALE][key] ?? key,
      pick: (text: BiText | null | undefined) => pick(text, locale),
      money: (v: number | null | undefined) => formatMoney(v, locale),
      ratio: (v: number, digits = 1) => formatRatio(v, digits),
      num: (v: number) => formatInt(v, locale),
      date: (iso: string) => formatDate(iso, locale),
    }),
    [locale, path, navigate, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n 必须在 I18nProvider 内使用');
  return ctx;
}

/** [gemini-i18n-core] 升级 8 国语言优雅切换菜单，带 aria-pressed 与完整键盘无障碍支持 */
export function LocaleToggle() {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('click', onDocClick);
      document.addEventListener('keydown', onKeyDown);
    }
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const current = LOCALE_CONFIG[locale] ?? LOCALE_CONFIG.zh;

  return (
    <div ref={containerRef} className="relative inline-block text-left" role="group" aria-label={t('lang.switchTo')}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1 text-xs font-medium text-body transition-colors hover:border-line-strong hover:bg-surface-2 focus:outline-none focus:ring-1 focus:ring-primary/40"
      >
        <span aria-hidden="true">{current.flag}</span>
        <span>{current.name}</span>
        <svg
          aria-hidden="true"
          className={`h-3 w-3 text-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('lang.switchTo')}
          className="absolute right-0 z-50 mt-1.5 w-44 origin-top-right rounded-lg border border-line bg-surface p-1 shadow-lg ring-1 ring-black/5"
        >
          {LOCALES.map((l) => {
            const meta = LOCALE_CONFIG[l];
            const active = locale === l;
            return (
              <button
                key={l}
                type="button"
                onClick={() => {
                  setLocale(l);
                  setOpen(false);
                }}
                aria-pressed={active}
                className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                  active
                    ? 'bg-primary/10 font-semibold text-primary-light'
                    : 'text-body hover:bg-surface-2'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden="true">{meta.flag}</span>
                  <span>{meta.name}</span>
                </span>
                {active && (
                  <span className="text-[10px] text-primary" aria-hidden="true">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * 中文界面层（站群通用）。
 *
 * longnan 分支起，站群为**中文单语站**：站点字典与业务数据只存 `zh`。
 * 但页眉、页脚、法定信息、免责声明等共享组件读的是共享字典 `MESSAGES`——
 * 若不处理，当 hash 带语言前缀（如 `#/vi/…`）时页脚会显示越南语，与管理口径不符。
 *
 * 因此在 `I18nProvider` 之内再套一层 Provider，**只覆盖文案取值函数**，
 * `locale` / `setLocale` / `navigate` 原样透传：语言前缀与链接可分享性不受影响，
 * 但页面所有文案（含共享组件）恒定取中文。
 */
export function ChineseSurface({ children }: { children: ReactNode }) {
  const base = useI18n();
  const value = useMemo<I18nValue>(
    () => ({
      ...base,
      t: (key: string) => MESSAGES.zh?.[key] ?? key,
      pick: (text: BiText | null | undefined) => pick(text, 'zh'),
      money: (v: number | null | undefined) => formatMoney(v, 'zh'),
      ratio: (v: number, digits = 1) => formatRatio(v, digits),
      num: (v: number) => formatInt(v, 'zh'),
      date: (iso: string) => formatDate(iso, 'zh'),
    }),
    [base],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
